import { useContext } from "react";
import { ActionCableConsumer } from "react-actioncable-provider";
import { FiBell, FiMessageCircle } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { BooleanParam, useQueryParams } from "use-query-params";

import FlexBox from "../../components/common/FlexBox";
import { H2 } from "../../components/common/Headings";
import IconWithCount from "../../components/common/IconWithCount";
import { SuperviseeProfileContext } from "../../context/SuperviseeProfileProvider";
import { trackEvent } from "../../helperFunctions";
import { useChatStatus } from "../../hooks/useChatStatus";
import useFeatureFlags from "../../hooks/useFeatureFlags";
import { useProviderChatStatus } from "../../hooks/useProviderChatStatus";
import {
  setHasNewNotification,
  setUnreadNotificationsCount,
} from "../../Store/Actions";
import {
  CF_PROVIDER_ROLE,
  channel,
  notificationsUnreadCountChannel,
  TYPE_PSYCHIATRIST,
} from "../../variable";

const Container = styled(FlexBox)`
  height: 4.5rem;
  padding: 1.25rem 1rem;
  justify-content: space-between;
  align-items: center;
`;

const ProviderImage = styled.img`
  flex-shrink: 0;
  width: 2.5rem;
  height: 2.5rem;
  object-fit: cover;
  border-radius: 50%;
  overflow: hidden;
  cursor: pointer;
`;

const MobileHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  let actionCable;

  const user = useSelector(state => state.auth?.user);
  const providerProfile = useSelector(state => state.provider.providerProfile);
  const mobileUnreadMessageCount = useSelector(
    state => state.chat?.mobileUnreadChatCount
  );
  const newChatUnreadRoomCount = useSelector(
    state => state.chat?.newChatUnreadRoomIds?.length || 0
  );
  const isNewChatEnabled = !!providerProfile?.new_chat_enabled;
  const isCfProvider = providerProfile?.roles?.some(role =>
    CF_PROVIDER_ROLE.includes(role)
  );

  const [, setQueryParams] = useQueryParams({
    showChatModal: BooleanParam,
  });

  const userFBId = user?.firebaseid;
  const reduxChatList = useSelector(state => state?.chat?.chatList);
  const { chatStatus } = useChatStatus(userFBId);
  const { providerChatStatus } = useProviderChatStatus(userFBId);
  const isOffline = chatStatus?.toLowerCase() === "offline";
  const unread_chat_count = reduxChatList?.filter(
    chat => chat.client && chat.client.unreadCount > 0
  );
  const unreadChatCount = isNewChatEnabled
    ? newChatUnreadRoomCount
    : unread_chat_count.length;

  const hasUnreadNotification = useSelector(
    state => state.notifications.hasUnreadNotification
  );

  const { isSupervisionMode } = useContext(SuperviseeProfileContext);

  const [isChatEnabled, isNotificationsEnabled] = useFeatureFlags([
    "twa_private_chat_clients",
    "twa_private_notifications",
  ]);

  const isClientSection = location.pathname?.includes("clients");
  const isGroupsSection = location.pathname?.includes("groups");
  const isDashboardRoute = location.pathname.includes("dashboard");

  const showProfileOptions = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage("openProfileBottomSheet");
    }
  };

  const handleNotificationClick = () => {
    const userName = `${user?.firstname || ""} ${user?.lastname || ""}`;
    trackEvent({
      event: "notification_bell_click",
      payload: {
        provider_name: userName,
        service_type:
          user?.usertype === TYPE_PSYCHIATRIST ? "psychiatry" : "therapy",
      },
    });
    navigate("/notifications");
  };

  const chatEventPayload = {
    unread_chat_count: unreadChatCount,
    chat_availability: isOffline ? "Offline" : "Online",
    chat_status: providerChatStatus || "null",
    is_auto_reply_sent:
      (providerChatStatus &&
        !["Available", "In Session"].includes(providerChatStatus)) ||
      false,
    source: isDashboardRoute ? "dashboard" : "clients_tab",
  };

  return (
    <Container>
      {!isSupervisionMode &&
        isNotificationsEnabled &&
        !isCfProvider &&
        (!!actionCable ||
          (actionCable = (
            <>
              <ActionCableConsumer
                channel={{ channel: channel }}
                onReceived={({ data }) => {
                  if (data?.user_id === user?.id) {
                    dispatch(setHasNewNotification(true));
                  }
                }}
              />
              <ActionCableConsumer
                channel={{ channel: notificationsUnreadCountChannel }}
                onReceived={({ data }) => {
                  dispatch(setUnreadNotificationsCount(data));
                }}
              />
            </>
          )))}
      <FlexBox columnGap="1rem" align="center">
        {isClientSection ? (
          <H2 bold>Clients</H2>
        ) : isGroupsSection ? (
          <H2 bold>Groups</H2>
        ) : (
          <>
            <ProviderImage
              alt="Profile Image"
              draggable="false"
              id="header-profile-image"
              src={"https:" + user?.image}
              onClick={showProfileOptions}
            />
            <H2 bold>Dashboard</H2>
          </>
        )}
      </FlexBox>
      <FlexBox columnGap="1rem" align="center">
        {isChatEnabled && (
          <IconWithCount
            count={
              isNewChatEnabled
                ? newChatUnreadRoomCount
                : mobileUnreadMessageCount
            }
            Icon={FiMessageCircle}
            onClick={() => {
              trackEvent({
                event: "twa_chat_cta_click",
                payload: chatEventPayload,
              });
              setQueryParams({ showChatModal: true });
            }}
          />
        )}
        {!isCfProvider && isNotificationsEnabled && (
          <IconWithCount
            Icon={FiBell}
            count={hasUnreadNotification}
            onClick={handleNotificationClick}
          />
        )}
      </FlexBox>
    </Container>
  );
};

export default MobileHeader;
